import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import esES from './translations/es-ES.json';
import { LanguageCode } from './languages';

interface TranslationDictionary {
  [key: string]: string | TranslationDictionary;
}

export type TranslationParams = Record<string, string | number> & { count?: string | number };

const LANGUAGE_STORAGE_KEY = 'app_language';

const LANGUAGE_LOADERS: Record<LanguageCode, () => Promise<TranslationDictionary>> = {
  'es-ES': async () => esES,
  en: () => import('./translations/en.json').then((module) => module.default),
  ca: () => import('./translations/ca.json').then((module) => module.default),
  fr: () => import('./translations/fr.json').then((module) => module.default),
  it: () => import('./translations/it.json').then((module) => module.default),
  pt: () => import('./translations/pt.json').then((module) => module.default),
  de: () => import('./translations/de.json').then((module) => module.default),
};

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly platformId = inject(PLATFORM_ID);

  private readonly dictionaries = new Map<LanguageCode, TranslationDictionary>([['es-ES', esES]]);

  private readonly currentLanguage = signal<LanguageCode>(this.loadStoredLanguage());
  private readonly translations = signal<TranslationDictionary>(esES);

  readonly language = computed(() => this.currentLanguage());

  constructor() {
    void this.setLanguage(this.currentLanguage());
  }

  async setLanguage(language: LanguageCode): Promise<void> {
    if (!LANGUAGE_LOADERS[language]) {
      language = 'es-ES';
    }

    const dictionary = await this.loadDictionary(language);
    this.currentLanguage.set(language);
    this.translations.set(dictionary);
    this.persistLanguage(language);
  }

  translate(key: string, params: TranslationParams = {}, count?: number): string {
    const pluralKey = typeof count === 'number' ? this.resolvePluralKey(key, count) : key;
    const translation = this.getStringValue(pluralKey) ?? this.getStringValue(key);

    if (typeof translation !== 'string') {
      return key;
    }

    const finalParams: TranslationParams = { ...params };

    if (typeof count === 'number' && finalParams.count === undefined) {
      finalParams.count = count;
    }

    return translation.replace(/{{\s*(\w+)\s*}}/g, (_, paramKey: string) => {
      const value = finalParams[paramKey];
      return value !== undefined ? String(value) : `{{${paramKey}}}`;
    });
  }

  getCurrentLanguage(): LanguageCode {
    return this.currentLanguage();
  }

  private async loadDictionary(language: LanguageCode): Promise<TranslationDictionary> {
    const cached = this.dictionaries.get(language);

    if (cached) {
      return cached;
    }

    const loader = LANGUAGE_LOADERS[language];
    const dictionary = loader ? await loader() : esES;
    this.dictionaries.set(language, dictionary);
    return dictionary;
  }

  private getValueByPath(
    dictionary: TranslationDictionary,
    key: string
  ): string | TranslationDictionary | undefined {
    return key.split('.').reduce<TranslationDictionary | string | undefined>((value, segment) => {
      if (typeof value !== 'object' || value === null) {
        return undefined;
      }

      return (value as TranslationDictionary)[segment];
    }, dictionary);
  }

  private loadStoredLanguage(): LanguageCode {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as LanguageCode | null;
      if (stored && LANGUAGE_LOADERS[stored]) {
        return stored;
      }
    }

    return 'es-ES';
  }

  private persistLanguage(language: LanguageCode): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }
  }

  private resolvePluralKey(key: string, count: number): string {
    const pluralCategory = this.getPluralCategory(count);
    const categoryKey = `${key}.${pluralCategory}`;

    if (this.hasStringTranslation(categoryKey)) {
      return categoryKey;
    }

    const otherKey = `${key}.other`;
    if (pluralCategory !== 'other' && this.hasStringTranslation(otherKey)) {
      return otherKey;
    }

    return key;
  }

  private getPluralCategory(count: number): Intl.LDMLPluralRule | 'other' {
    try {
      const pluralRules = new Intl.PluralRules(this.currentLanguage());
      return pluralRules.select(count) ?? 'other';
    } catch {
      return 'other';
    }
  }

  private hasStringTranslation(key: string): boolean {
    return typeof this.getStringValue(key) === 'string';
  }

  private getStringValue(key: string): string | undefined {
    const value = this.getValueByPath(this.translations(), key);
    if (typeof value === 'string') {
      return value;
    }

    const fallback = this.getValueByPath(esES, key);
    return typeof fallback === 'string' ? fallback : undefined;
  }
}
